import { supabase } from '../config/supabase'

// ElevenLabs API types
interface ElevenLabsAgent {
  agent_id: string
  name: string
  created_at_unix_secs?: number
  access_info?: {
    is_creator: boolean
    creator_name: string
    creator_email: string
    role: string
  }
  conversation_config?: any
  phone_numbers?: any[]
}

interface ElevenLabsConversation {
  agent_id: string
  conversation_id: string
  start_time_unix_secs: number
  call_duration_secs?: number
  message_count?: number
  status: string
  call_successful?: string
  agent_name?: string
}

class AgentService {
  private apiKey: string

  constructor(apiKey: string) {
    this.apiKey = apiKey
  }

  // Get user's agents (filtered by user_id from Supabase)
  async getUserAgents(): Promise<ElevenLabsAgent[]> {
    try {
      // 1. Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      // 2. Get user's agent mappings from Supabase (RLS will filter automatically)
      const { data: userAgentMappings, error: dbError } = await supabase
        .from('agents')
        .select('*')
        .eq('user_id', user.id) // Ensure only the current user's agents are returned

      if (dbError) {
        console.error('Error fetching user agents from database:', dbError)
        return []
      }

      if (!userAgentMappings || userAgentMappings.length === 0) {
        return []
      }

      // 3. Get agent details from ElevenLabs for user's agents only
      const elevenlabsAgents: ElevenLabsAgent[] = []
      
      for (const mapping of userAgentMappings) {
        if (mapping.elevenlabs_agent_id) {
          try {
            const agent = await this.getElevenLabsAgentById(mapping.elevenlabs_agent_id)
            if (agent) {
              // Merge with local data
              elevenlabsAgents.push({
                ...agent,
                name: mapping.name || agent.name, // Use local name if available
              })
            }
          } catch (error) {
            console.error(`Error fetching agent ${mapping.elevenlabs_agent_id}:`, error)
            // Continue with other agents even if one fails
          }
        }
      }

      return elevenlabsAgents
    } catch (error) {
      console.error('Error in getUserAgents:', error)
      return []
    }
  }

  // Get conversations only for user's agents
  async getUserConversations(): Promise<ElevenLabsConversation[]> {
    try {
      // 1. Get user's agent IDs
      const userAgents = await this.getUserAgents()
      const userAgentIds = userAgents.map(agent => agent.agent_id)

      if (userAgentIds.length === 0) {
        return []
      }

      // 2. Fetch all conversations from ElevenLabs
      const response = await fetch('https://api.elevenlabs.io/v1/convai/conversations', {
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.statusText}`)
      }

      const data = await response.json()
      const allConversations = data.conversations || []

      // 3. Filter conversations to only include user's agents
      return allConversations.filter((conversation: ElevenLabsConversation) =>
        userAgentIds.includes(conversation.agent_id)
      )
    } catch (error) {
      console.error('Error in getUserConversations:', error)
      return []
    }
  }

  // Create agent and store mapping
  async createAgent(agentData: any): Promise<ElevenLabsAgent | null> {
    try {
      // 1. Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      // 2. Create agent in ElevenLabs
      console.log('Sending agent creation request to ElevenLabs:', agentData);
      const response = await fetch('https://api.elevenlabs.io/v1/convai/agents/create', {
        method: 'POST',
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(agentData),
      })

      if (!response.ok) {
        const errorText = await response.text();
        console.error('ElevenLabs API error response:', errorText);
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(`Failed to create agent: ${errorJson.detail || errorJson.message || response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to create agent: ${response.statusText} - ${errorText}`);
        }
      }

      const elevenlabsResponse = await response.json()
      
      // 3. Store mapping in Supabase
      const { error: dbError } = await supabase
        .from('agents')
        .insert({
          user_id: user.id,
          elevenlabs_agent_id: elevenlabsResponse.agent_id,
          name: agentData.name,
          local_settings: {}
        })

      if (dbError) {
        console.error('Error storing agent mapping:', dbError)
        // Agent was created in ElevenLabs but mapping failed
        // You might want to handle this case differently
      }

      return elevenlabsResponse
    } catch (error) {
      console.error('Error in createAgent:', error)
      return null
    }
  }

  // Delete agent and remove mapping
  async deleteAgent(agentId: string): Promise<boolean> {
    try {
      // 1. Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      // 2. Verify user owns this agent
      const { data: agentMapping, error: dbError } = await supabase
        .from('agents')
        .select('*')
        .eq('elevenlabs_agent_id', agentId)
        .single()

      if (dbError || !agentMapping) {
        throw new Error('Agent not found or not owned by user')
      }

      // 3. Delete from ElevenLabs
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${agentId}`, {
        method: 'DELETE',
        headers: {
          'xi-api-key': this.apiKey,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to delete agent: ${response.statusText}`)
      }

      // 4. Remove mapping from Supabase
      const { error: deleteError } = await supabase
        .from('agents')
        .delete()
        .eq('elevenlabs_agent_id', agentId)

      if (deleteError) {
        console.error('Error removing agent mapping:', deleteError)
      }

      return true
    } catch (error) {
      console.error('Error in deleteAgent:', error)
      return false
    }
  }

  // Remove agent from user's list without deleting from ElevenLabs
  async removeAgent(agentId: string): Promise<boolean> {
    try {
      // 1. Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      // 2. Verify user owns this agent
      const { data: agentMapping, error: dbError } = await supabase
        .from('agents')
        .select('*')
        .eq('elevenlabs_agent_id', agentId)
        .eq('user_id', user.id)
        .single()

      if (dbError || !agentMapping) {
        throw new Error('Agent not found or not owned by user')
      }

      // 3. Remove mapping from Supabase (agent remains in ElevenLabs)
      const { error: deleteError } = await supabase
        .from('agents')
        .delete()
        .eq('elevenlabs_agent_id', agentId)
        .eq('user_id', user.id)

      if (deleteError) {
        console.error('Error removing agent mapping:', deleteError)
        throw new Error('Failed to remove agent from your account')
      }

      return true
    } catch (error) {
      console.error('Error in removeAgent:', error)
      return false
    }
  }

  // Private helper to get single agent from ElevenLabs
  private async getElevenLabsAgentById(agentId: string): Promise<ElevenLabsAgent | null> {
    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${agentId}`, {
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch agent: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error(`Error fetching agent ${agentId}:`, error)
      return null
    }
  }

  // Update agent in ElevenLabs
  async updateAgent(agentId: string, agentData: any): Promise<ElevenLabsAgent | null> {
    try {
      // 1. Verify user owns this agent
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      const { data: agentMapping, error: dbError } = await supabase
        .from('agents')
        .select('*')
        .eq('elevenlabs_agent_id', agentId)
        .single()

      if (dbError || !agentMapping) {
        throw new Error('Agent not found or not owned by user')
      }

      // 2. Update in ElevenLabs
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${agentId}`, {
        method: 'PATCH',
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(agentData),
      })

      if (!response.ok) {
        throw new Error(`Failed to update agent: ${response.statusText}`)
      }

      // 3. Update local mapping if name changed
      if (agentData.name && agentData.name !== agentMapping.name) {
        await supabase
          .from('agents')
          .update({ name: agentData.name })
          .eq('elevenlabs_agent_id', agentId)
      }

      return await response.json()
    } catch (error) {
      console.error('Error in updateAgent:', error)
      return null
    }
  }
}

export default AgentService 